import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { App as AntdApp, ConfigProvider } from 'antd';
import viVN from 'antd/locale/vi_VN';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux';

dayjs.locale('vi');

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { colorPrimary, sidebarBg, sidebarActiveBg, sidebarText } = useSelector(
    (state: RootState) => state.theme.customTokens
  );

  return (
    <ConfigProvider
      locale={viVN}
      theme={{
        token: {
          colorPrimary: colorPrimary,
          colorLink: colorPrimary,
          colorTextLightSolid: '#ffffff',
          // TCR-2423 — màu ngữ nghĩa mặc định của antd quá nhạt khi dùng làm CHỮ hoặc làm NỀN
          // cho chữ trắng (badge đếm, Statistic, token.colorSuccess/colorError ở ~126 chỗ trong code):
          // success #52c41a 2.3:1, error #ff4d4f 3.3:1, info #1677ff 4.1:1, warning #faad14 1.9:1.
          // Chọn cấp đậm của chính bảng màu antd để đạt >= 4.5:1 trên nền trắng (và chữ trắng trên nền
          // màu đó): #237804 (5.6), #cf1322 (5.6), #0958d9 (6.2), #9c5a00 (5.4).
          colorSuccess: '#237804',
          colorError: '#cf1322',
          colorInfo: '#0958d9',
          colorWarning: '#9c5a00',
          // #707070: chữ trong ô input/select bị disabled (nền #f5f5f5) đạt ~4.5:1 thay vì
          // ~3.1:1 của #8c8c8c — xem giải thích đầy đủ ở src/styles/theme.ts. Dấu "•••" của
          // phân trang cũng dùng token này nên hết bị mờ.
          colorTextDisabled: '#707070',
          colorBgContainerDisabled: '#f5f5f5',
          borderRadius: 6,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          /*
           * TCR-2422 — đây mới là theme ĐANG chạy (src/styles/theme.ts không được
           * import ở đâu), nên phải khai báo lại cùng bộ token ở đây, nếu không
           * thay đổi chỉ nằm trên giấy. Màu đặc thay cho alpha mặc định của antd;
           * lý do chọn từng mức xám xem comment chi tiết ở src/styles/theme.ts.
           */
          colorText: '#1f1f1f',
          colorTextSecondary: '#4a4a4a',
          colorTextTertiary: '#6b6b6b',
          // #767676: nền badge/đếm xám có chữ trắng đạt ~4.5:1 (xem theme.ts).
          colorTextQuaternary: '#767676',
          colorTextDescription: '#6b6b6b',
          colorTextPlaceholder: '#6b6b6b',
          colorTextHeading: '#141414',
          colorTextLabel: '#262626',
          colorBorder: '#bfbfbf',
          colorBorderSecondary: '#d9d9d9',
          fontSize: 14,
        },
        components: {
          Layout: {
            siderBg: sidebarBg,
          },
          Menu: {
            darkItemBg: sidebarBg,
            darkItemSelectedBg: sidebarActiveBg,
            darkItemColor: sidebarText,
          },
        },
      }}
    >
      <style>{`
        :root {
          --sidebar-bg: ${sidebarBg};
          --sidebar-active-bg: ${sidebarActiveBg};
          --sidebar-text: ${sidebarText};
          --color-primary: ${colorPrimary};
        }

        /*
         * Chỉ nút primary NỀN ĐẶC mới cần chữ trắng. Nút ghost/link/text vẫn
         * mang class .ant-btn-primary nhưng nền trong suốt — dính luật này là
         * chữ trắng trên nền trắng, người dùng chỉ thấy cái khung rỗng.
         */
        .ant-btn-primary:not(:disabled):not(.ant-btn-disabled):not(.ant-btn-background-ghost):not(.ant-btn-link):not(.ant-btn-text) {
          color: #ffffff !important;
        }

        .ant-btn-primary:not(:disabled):not(.ant-btn-disabled):not(.ant-btn-background-ghost):not(.ant-btn-link):not(.ant-btn-text):hover,
        .ant-btn-primary:not(:disabled):not(.ant-btn-disabled):not(.ant-btn-background-ghost):not(.ant-btn-link):not(.ant-btn-text):focus {
          color: #ffffff !important;
        }

        .ant-btn:disabled,
        .ant-btn[disabled],
        .ant-btn.ant-btn-disabled,
        .ant-btn-primary:disabled,
        .ant-btn-primary[disabled],
        .ant-btn-primary.ant-btn-disabled {
          color: #6b6b6b !important;
          background: #f5f5f5 !important;
          border-color: #d9d9d9 !important;
          text-shadow: none !important;
          box-shadow: none !important;
        }

        .ant-btn:disabled .anticon,
        .ant-btn[disabled] .anticon,
        .ant-btn.ant-btn-disabled .anticon {
          color: #6b6b6b !important;
        }
      `}</style>
      {/*
        * `App` cấp context cho `App.useApp()`: hộp thoại/thông báo mở qua hook
        * mới ăn theo token động ở trên. Hàm static `Modal.confirm` nằm ngoài
        * React tree nên luôn rơi về màu mặc định của antd (xanh #1677ff) —
        * lệch hẳn với brand đỏ.
        */}
      <AntdApp component={false}>{children}</AntdApp>
    </ConfigProvider>
  );
};
